// KnowledgeBaseTab.jsx
// Admin-Tab für die HejVi Domain Knowledge Base.
// Nur sichtbar für user.group === 0 (Super-Admin).
//
// Einbindung in AdminPanel.js:
//   import KnowledgeBaseTab from './KnowledgeBaseTab';
//   // Im Tab-Array:
//   { id: 'knowledge', label: '🧠 Wissensbasis', component: <KnowledgeBaseTab makeAuthenticatedRequest={makeAuthenticatedRequest} /> }

import { useState, useEffect, useCallback } from 'react';

// ─── Such-Helper ───────────────────────────────────────────────────────────────
function useSearch() {
  const [q, setQ] = useState('');
  const matches   = useCallback((terms) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return terms.some(t => String(t ?? '').toLowerCase().includes(needle));
  }, [q]);
  return { q, setQ, matches };
}

function SearchBar({ q, setQ, placeholder = 'Suchen…', count, total }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
      <div style={{ position: 'relative', flex: 1 }}>
        <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 14, pointerEvents: 'none' }}>🔍</span>
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder={placeholder}
          style={{
            width: '100%', boxSizing: 'border-box',
            border: '1px solid #e2e8f0', borderRadius: 8,
            padding: '7px 12px 7px 32px', fontSize: 13, outline: 'none',
          }}
        />
        {q && (
          <span
            onClick={() => setQ('')}
            style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#94a3b8', fontSize: 16 }}
          >×</span>
        )}
      </div>
      {q && (
        <span style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
          {count} / {total}
        </span>
      )}
    </div>
  );
}

// ─── API-Helfer ────────────────────────────────────────────────────────────────
const useKnowledgeApi = (makeAuthenticatedRequest) => {
  const call = useCallback(async (method, path, body = null, isFormData = false) => {
    const opts = { method };
    if (body && !isFormData) {
      opts.headers = { 'Content-Type': 'application/json' };
      opts.body = JSON.stringify(body);
    } else if (body && isFormData) {
      opts.body = body; // FormData
    }
    return makeAuthenticatedRequest(`/domain-knowledge/${path}`, opts);
  }, [makeAuthenticatedRequest]);

  return { call };
};

// ─── Haupt-Komponente ──────────────────────────────────────────────────────────
export default function KnowledgeBaseTab({ makeAuthenticatedRequest }) {
  const [activeSection, setActiveSection] = useState('manufacturers');
  const [toast, setToast]                 = useState(null);
  const [counts, setCounts]               = useState({});

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // Alle Counts neu laden — wird nach Import aufgerufen
  const reloadCounts = useCallback(async () => {
    try {
      const [mfr, ot, tr, sug, syn] = await Promise.allSettled([
        makeAuthenticatedRequest('/domain-knowledge/manufacturers'),
        makeAuthenticatedRequest('/domain-knowledge/object-types'),
        makeAuthenticatedRequest('/domain-knowledge/trades'),
        makeAuthenticatedRequest('/domain-knowledge/suggestions'),
        makeAuthenticatedRequest('/domain-knowledge/synonyms'),
      ]);
      setCounts({
        manufacturers:  mfr.status === 'fulfilled' ? (mfr.value.manufacturers?.length ?? 0) : null,
        'object-types': ot.status  === 'fulfilled' ? (ot.value.object_types?.length   ?? 0) : null,
        trades:         tr.status  === 'fulfilled' ? (tr.value.trades?.length          ?? 0) : null,
        suggestions:    sug.status === 'fulfilled' ? (sug.value.suggestions?.length   ?? 0) : null,
        synonyms:       syn.status === 'fulfilled' ? (syn.value.synonyms?.length      ?? 0) : null,
      });
    } catch { /* silent */ }
  }, [makeAuthenticatedRequest]);

  // Einmal beim Mount alle Counts laden
  useEffect(() => { reloadCounts(); }, [reloadCounts]);

  // Callback den Sections übergeben, damit Count nach Add/Delete aktuell bleibt
  const updateCount = useCallback((section, n) => {
    setCounts(c => ({ ...c, [section]: n }));
  }, []);

  // Stabile Callbacks — NICHT inline in JSX, sonst Loop:
  // jede setCounts-Aktualisierung → KBT re-rendert → neue Arrow-Fn →
  // load() in Section recreated → useEffect feuert → onCountChange → setCounts → repeat
  const onMfrCountChange = useCallback(n => updateCount('manufacturers', n), [updateCount]);
  const onOtCountChange  = useCallback(n => updateCount('object-types',  n), [updateCount]);
  const onTrCountChange  = useCallback(n => updateCount('trades',        n), [updateCount]);
  const onSynCountChange = useCallback(n => updateCount('synonyms',      n), [updateCount]);
  const onSugCountChange = useCallback(n => updateCount('suggestions',   n), [updateCount]);

  const sections = [
    { id: 'manufacturers', label: '🏭 Hersteller'   },
    { id: 'object-types',  label: '📦 Objekt-Typen' },
    { id: 'trades',        label: '🔧 Gewerke'       },
    { id: 'synonyms',      label: '🔍 Synonyme'      },
    { id: 'suggestions',   label: '💡 Vorschläge'    },
    { id: 'import',        label: '📥 Import'        },
  ];

  return (
    <div style={{ fontFamily: 'inherit' }}>
      {/* Abschnitts-Navigation — padding/fontSize klein damit 6 Buttons in 2 Zeilen passen */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
        {sections.map(s => {
          const active = activeSection === s.id;
          const count  = counts[s.id];
          return (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 10px', borderRadius: 7, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 500,
                background: active ? '#2563eb' : '#f1f5f9',
                color:      active ? '#fff'    : '#374151',
              }}
            >
              {s.label}
              {count != null && (
                <span style={{
                  background:   active ? 'rgba(255,255,255,0.25)' : '#e2e8f0',
                  color:        active ? '#fff' : '#64748b',
                  borderRadius: 99, fontSize: 11, fontWeight: 700,
                  padding: '1px 6px', lineHeight: '17px', minWidth: 18, textAlign: 'center',
                }}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Abschnitt-Inhalt */}
      {activeSection === 'manufacturers' && <ManufacturersSection makeReq={makeAuthenticatedRequest} showToast={showToast} onCountChange={onMfrCountChange} />}
      {activeSection === 'object-types'  && <ObjectTypesSection  makeReq={makeAuthenticatedRequest} showToast={showToast} onCountChange={onOtCountChange} />}
      {activeSection === 'trades'        && <TradesSection        makeReq={makeAuthenticatedRequest} showToast={showToast} onCountChange={onTrCountChange} />}
      {activeSection === 'synonyms'      && <SynonymsSection      makeReq={makeAuthenticatedRequest} showToast={showToast} onCountChange={onSynCountChange} />}
      {activeSection === 'suggestions'   && <SuggestionsSection  makeReq={makeAuthenticatedRequest} showToast={showToast} onCountChange={onSugCountChange} />}
      {activeSection === 'import'        && <ImportSection        makeReq={makeAuthenticatedRequest} showToast={showToast} onImportDone={reloadCounts} onSwitchTo={setActiveSection} />}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          background: toast.type === 'success' ? '#16a34a' : '#dc2626',
          color: '#fff', padding: '10px 18px', borderRadius: 10,
          boxShadow: '0 4px 16px rgba(0,0,0,.18)', fontSize: 14,
        }}>{toast.msg}</div>
      )}
    </div>
  );
}

// ─── Hersteller ───────────────────────────────────────────────────────────────
function ManufacturersSection({ makeReq, showToast, onCountChange }) {
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [editId, setEditId]               = useState(null);
  const [showAdd, setShowAdd]             = useState(false);
  const [newAlias, setNewAlias]           = useState({});
  const { q, setQ, matches }              = useSearch();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await makeReq('/domain-knowledge/manufacturers');
      const mfrs = data.manufacturers ?? [];
      setManufacturers(mfrs);
      onCountChange?.(mfrs.length);
    } catch { showToast('Ladefehler', 'error'); }
    finally { setLoading(false); }
  }, [makeReq, showToast, onCountChange]);

  useEffect(() => { load(); }, [load]);

  const deleteMfr = async (id) => {
    if (!window.confirm('Hersteller und alle Aliases löschen?')) return;
    await makeReq(`/domain-knowledge/manufacturers/${id}`, { method: 'DELETE' });
    showToast('Hersteller gelöscht');
    load();
  };

  const addAlias = async (mfrId) => {
    const alias = (newAlias[mfrId] ?? '').trim();
    if (!alias) return;
    await makeReq(`/domain-knowledge/manufacturers/${mfrId}/aliases`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ alias }),
    });
    setNewAlias(a => ({ ...a, [mfrId]: '' }));
    showToast('Alias hinzugefügt');
    load();
  };

  const deleteAlias = async (aliasId) => {
    await makeReq(`/domain-knowledge/manufacturers/aliases/${aliasId}`, { method: 'DELETE' });
    showToast('Alias entfernt');
    load();
  };

  const toggleVerified = async (mfr) => {
    await makeReq(`/domain-knowledge/manufacturers/${mfr.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ verified: !mfr.verified }),
    });
    load();
  };

  if (loading) return <Spinner />;

  const filtered = manufacturers.filter(mfr =>
    matches([mfr.canonical_name, mfr.description, mfr.source, ...mfr.aliases.map(a => a.alias)])
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Hersteller ({manufacturers.length})</h3>
        <Btn onClick={() => setShowAdd(true)} primary>+ Hersteller</Btn>
      </div>

      <SearchBar q={q} setQ={setQ} placeholder="Hersteller oder Alias suchen…" count={filtered.length} total={manufacturers.length} />

      {showAdd && (
        <AddManufacturerForm
          makeReq={makeReq}
          onSaved={() => { setShowAdd(false); load(); showToast('Hersteller angelegt'); }}
          onCancel={() => setShowAdd(false)}
        />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filtered.length === 0 && q && (
          <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>Keine Treffer für „{q}"</div>
        )}
        {filtered.map(mfr => (
          <div key={mfr.id} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px' }}>

            {/* Zeile 1: Name+Badge links, Buttons rechts — Buttons niemals umbrechen */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              {/* Links: Name + Badges — dürfen umbrechen */}
              <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{mfr.canonical_name}</span>
                <span style={{
                  fontSize: 11, padding: '2px 7px', borderRadius: 99,
                  background: mfr.verified ? '#dcfce7' : '#fef9c3',
                  color:      mfr.verified ? '#15803d' : '#92400e',
                }}>{mfr.verified ? '✓ Verifiziert' : '⏳ Ausstehend'}</span>
                <span style={{ fontSize: 11, color: '#cbd5e1' }}>{mfr.source} #{mfr.id}</span>
              </div>
              {/* Rechts: Buttons — immer zusammen, nie umbrechen */}
              <div style={{ display: 'flex', gap: 6, flexShrink: 0, paddingTop: 1 }}>
                <Btn small onClick={() => toggleVerified(mfr)}>
                  {mfr.verified ? 'Unverifizieren' : 'Verifizieren'}
                </Btn>
                <Btn small danger onClick={() => deleteMfr(mfr.id)}>Löschen</Btn>
              </div>
            </div>

            {/* Zeile 2: Aliases */}
            <div style={{ marginTop: 7, display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
              {mfr.aliases.map(a => (
                <span key={a.id} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  background: a.source === 'ai' ? '#ede9fe' : '#e0f2fe',
                  color:      a.source === 'ai' ? '#5b21b6' : '#0369a1',
                  borderRadius: 99, fontSize: 12, padding: '2px 9px',
                }}>
                  {a.alias}
                  <span style={{ cursor: 'pointer', opacity: .5, lineHeight: 1 }} onClick={() => deleteAlias(a.id)}>×</span>
                </span>
              ))}
              <input
                value={newAlias[mfr.id] ?? ''}
                onChange={e => setNewAlias(a => ({ ...a, [mfr.id]: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && addAlias(mfr.id)}
                placeholder="+ Alias"
                style={{ border: '1px dashed #cbd5e1', borderRadius: 99, padding: '2px 10px', fontSize: 12, width: 110, outline: 'none' }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AddManufacturerForm({ makeReq, onSaved, onCancel }) {
  const [name, setName]       = useState('');
  const [desc, setDesc]       = useState('');
  const [aliases, setAliases] = useState('');
  const [saving, setSaving]   = useState(false);

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await makeReq('/domain-knowledge/manufacturers', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        canonical_name: name.trim(),
        description:    desc.trim() || null,
        aliases:        aliases.split(',').map(s => s.trim()).filter(Boolean),
      }),
    });
    setSaving(false);
    onSaved();
  };

  return (
    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 16, marginBottom: 16 }}>
      <h4 style={{ margin: '0 0 12px', fontSize: 14 }}>Neuer Hersteller</h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Kanonischer Name (z.B. bito)" style={inputStyle} />
        <input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Kurzbeschreibung (optional)" style={inputStyle} />
        <input value={aliases} onChange={e => setAliases(e.target.value)} placeholder="Aliases kommagetrennt (z.B. bito AG, Bito Aktiengesellschaft)" style={inputStyle} />
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn primary onClick={save} disabled={saving}>{saving ? 'Speichert…' : 'Speichern'}</Btn>
          <Btn onClick={onCancel}>Abbrechen</Btn>
        </div>
      </div>
    </div>
  );
}

// ─── Objekt-Typen ─────────────────────────────────────────────────────────────
function ObjectTypesSection({ makeReq, showToast, onCountChange }) {
  const [objectTypes, setObjectTypes] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [expandedId, setExpandedId]   = useState(null);
  const [variants, setVariants]       = useState({});
  const [showAdd, setShowAdd]         = useState(false);
  const [showAddVariant, setShowAddVariant] = useState(null);
  const { q, setQ, matches }          = useSearch();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await makeReq('/domain-knowledge/object-types');
      const ots = data.object_types ?? [];
      setObjectTypes(ots);
      onCountChange?.(ots.length);
    } catch { showToast('Ladefehler', 'error'); }
    finally { setLoading(false); }
  }, [makeReq, showToast]);

  useEffect(() => { load(); }, [load]);

  const loadVariants = async (typeId) => {
    if (variants[typeId]) { setExpandedId(typeId); return; }
    const data = await makeReq(`/domain-knowledge/object-types/${typeId}/variants`);
    setVariants(v => ({ ...v, [typeId]: data.variants ?? [] }));
    setExpandedId(typeId);
  };

  const deleteType = async (id) => {
    if (!window.confirm('Objekt-Typ und alle Varianten löschen?')) return;
    await makeReq(`/domain-knowledge/object-types/${id}`, { method: 'DELETE' });
    showToast('Objekt-Typ gelöscht');
    load();
  };

  const deleteVariant = async (typeId, variantId) => {
    await makeReq(`/domain-knowledge/variants/${variantId}`, { method: 'DELETE' });
    showToast('Variante gelöscht');
    const data = await makeReq(`/domain-knowledge/object-types/${typeId}/variants`);
    setVariants(v => ({ ...v, [typeId]: data.variants ?? [] }));
  };

  if (loading) return <Spinner />;

  const filtered = objectTypes.filter(ot =>
    matches([ot.name, ot.manufacturer_name, ot.trade_name, ot.description, ...(ot.aliases ?? [])])
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Objekt-Typen ({objectTypes.length})</h3>
        <Btn primary onClick={() => setShowAdd(true)}>+ Objekt-Typ</Btn>
      </div>

      <SearchBar q={q} setQ={setQ} placeholder="Typ, Hersteller oder Alias suchen…" count={filtered.length} total={objectTypes.length} />

      {showAdd && (
        <AddObjectTypeForm
          makeReq={makeReq}
          onSaved={() => { setShowAdd(false); load(); showToast('Objekt-Typ angelegt'); }}
          onCancel={() => setShowAdd(false)}
        />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filtered.length === 0 && q && (
          <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>Keine Treffer für „{q}"</div>
        )}
        {filtered.map(ot => (
          <div key={ot.id} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>

            {/* Zeile 1: Expand + Name+Tags links — Buttons rechts */}
            <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* Links: klickbar zum Aufklappen */}
              <div
                style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, flex: 1, cursor: 'pointer' }}
                onClick={() => expandedId === ot.id ? setExpandedId(null) : loadVariants(ot.id)}
              >
                <span style={{ fontSize: 12, color: '#94a3b8' }}>{expandedId === ot.id ? '▼' : '▶'}</span>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{ot.name}</span>
                {ot.manufacturer_name && <span style={{ fontSize: 12, color: '#2563eb' }}>{ot.manufacturer_name}</span>}
                {ot.trade_name && <Tag>{ot.trade_name}</Tag>}
                {(ot.aliases ?? []).map((a, i) => <Tag key={i} muted>{a}</Tag>)}
                <span style={{ fontSize: 11, color: '#94a3b8' }}>{ot.variant_count} Var.</span>
              </div>
              {/* Rechts: Aktionen — immer fixiert */}
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <Btn small onClick={e => { e.stopPropagation(); setShowAddVariant(ot.id); }}>+ Variante</Btn>
                <Btn small danger onClick={e => { e.stopPropagation(); deleteType(ot.id); }}>Löschen</Btn>
              </div>
            </div>

            {/* Varianten (aufgeklappt) */}
            {expandedId === ot.id && (
              <div style={{ borderTop: '1px solid #e2e8f0', padding: '10px 14px 12px 28px', background: '#fff' }}>
                {showAddVariant === ot.id && (
                  <AddVariantForm
                    makeReq={makeReq}
                    objectTypeId={ot.id}
                    onSaved={async () => {
                      setShowAddVariant(null);
                      const data = await makeReq(`/domain-knowledge/object-types/${ot.id}/variants`);
                      setVariants(v => ({ ...v, [ot.id]: data.variants ?? [] }));
                      showToast('Variante angelegt');
                    }}
                    onCancel={() => setShowAddVariant(null)}
                  />
                )}
                {(variants[ot.id] ?? []).length === 0
                  ? <span style={{ fontSize: 13, color: '#94a3b8' }}>Keine Varianten</span>
                  : (variants[ot.id] ?? []).map(v => (
                    <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                      <span style={{ fontSize: 13, fontWeight: 500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.name}</span>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', flex: 2 }}>
                        {(v.aliases ?? []).map((a, i) => <Tag key={i} muted>{a}</Tag>)}
                        {Object.entries(v.attributes ?? {}).map(([k, val]) => (
                          <Tag key={k}>{k}: {val}</Tag>
                        ))}
                      </div>
                      <Btn small danger onClick={() => deleteVariant(ot.id, v.id)}>×</Btn>
                    </div>
                  ))
                }
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AddObjectTypeForm({ makeReq, onSaved, onCancel }) {
  const [name, setName]       = useState('');
  const [mfr, setMfr]         = useState('');
  const [aliases, setAliases] = useState('');
  const [desc, setDesc]       = useState('');
  const [saving, setSaving]   = useState(false);

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await makeReq('/domain-knowledge/object-types', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name:        name.trim(),
        description: desc.trim() || null,
        aliases:     aliases.split(',').map(s => s.trim()).filter(Boolean),
      }),
    });
    setSaving(false);
    onSaved();
  };

  return (
    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 16, marginBottom: 12 }}>
      <h4 style={{ margin: '0 0 10px', fontSize: 14 }}>Neuer Objekt-Typ</h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <input value={name}    onChange={e => setName(e.target.value)}    placeholder="Name (z.B. Porsche 911)" style={inputStyle} />
        <input value={aliases} onChange={e => setAliases(e.target.value)} placeholder="Aliases kommagetrennt (Elfer, Neunelfer, 911er)" style={inputStyle} />
        <input value={desc}    onChange={e => setDesc(e.target.value)}    placeholder="Beschreibung (optional)" style={inputStyle} />
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn primary onClick={save} disabled={saving}>{saving ? 'Speichert…' : 'Speichern'}</Btn>
          <Btn onClick={onCancel}>Abbrechen</Btn>
        </div>
      </div>
    </div>
  );
}

function AddVariantForm({ makeReq, objectTypeId, onSaved, onCancel }) {
  const [name, setName]       = useState('');
  const [aliases, setAliases] = useState('');
  const [attrs, setAttrs]     = useState('');  // JSON-String
  const [saving, setSaving]   = useState(false);
  const [attrErr, setAttrErr] = useState('');

  const save = async () => {
    if (!name.trim()) return;
    let parsedAttrs = {};
    if (attrs.trim()) {
      try { parsedAttrs = JSON.parse(attrs); } catch { setAttrErr('Ungültiges JSON'); return; }
    }
    setSaving(true);
    await makeReq(`/domain-knowledge/object-types/${objectTypeId}/variants`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name:       name.trim(),
        aliases:    aliases.split(',').map(s => s.trim()).filter(Boolean),
        attributes: parsedAttrs,
        verified:   true,
      }),
    });
    setSaving(false);
    onSaved();
  };

  return (
    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 12, marginBottom: 10 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        <input value={name}    onChange={e => setName(e.target.value)}    placeholder="Variantenname (z.B. G-Modell Targa 2.7)" style={inputStyle} />
        <input value={aliases} onChange={e => setAliases(e.target.value)} placeholder="Aliases (G-Modell, G-Serie)" style={inputStyle} />
        <input value={attrs}   onChange={e => { setAttrs(e.target.value); setAttrErr(''); }}
          placeholder='Attribute als JSON: {"Baujahr_von":"1974","Motor":"2.7"}' style={inputStyle} />
        {attrErr && <span style={{ color: '#dc2626', fontSize: 12 }}>{attrErr}</span>}
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn primary onClick={save} disabled={saving}>{saving ? '…' : 'Speichern'}</Btn>
          <Btn onClick={onCancel}>Abbrechen</Btn>
        </div>
      </div>
    </div>
  );
}

// ─── Gewerke ──────────────────────────────────────────────────────────────────
function TradesSection({ makeReq, showToast, onCountChange }) {
  const [trades, setTrades]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const { q, setQ, matches }  = useSearch();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await makeReq('/domain-knowledge/trades');
      const trs = data.trades ?? [];
      setTrades(trs);
      onCountChange?.(trs.length);
    } catch { showToast('Ladefehler', 'error'); }
    finally { setLoading(false); }
  }, [makeReq, showToast]);

  useEffect(() => { load(); }, [load]);

  const deleteTrade = async (id) => {
    if (!window.confirm('Gewerk löschen?')) return;
    await makeReq(`/domain-knowledge/trades/${id}`, { method: 'DELETE' });
    showToast('Gewerk gelöscht');
    load();
  };

  if (loading) return <Spinner />;

  // Baum-Struktur aufbauen
  const roots    = trades.filter(t => !t.parent_id);
  const children = (parentId) => trades.filter(t => String(t.parent_id) === String(parentId));

  // Bei aktiver Suche: flache gefilterte Liste statt Baum
  const flatFiltered = q ? trades.filter(t => matches([t.name, t.name_en])) : [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Gewerke ({trades.length})</h3>
        <Btn primary onClick={() => setShowAdd(!showAdd)}>+ Gewerk</Btn>
      </div>

      <SearchBar q={q} setQ={setQ} placeholder="Gewerk oder Branche suchen…" count={flatFiltered.length} total={trades.length} />

      {showAdd && (
        <AddTradeForm
          trades={trades}
          makeReq={makeReq}
          onSaved={() => { setShowAdd(false); load(); showToast('Gewerk angelegt'); }}
          onCancel={() => setShowAdd(false)}
        />
      )}

      {/* Suchergebnis: flache Liste */}
      {q ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {flatFiltered.length === 0 && (
            <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>Keine Treffer für „{q}"</div>
          )}
          {flatFiltered.map(t => {
            const parent = trades.find(p => String(p.id) === String(t.parent_id));
            return (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 14px' }}>
                {parent && <span style={{ fontSize: 11, color: '#94a3b8' }}>{parent.name} →</span>}
                <span style={{ fontWeight: 500, fontSize: 13 }}>{t.name}</span>
                {t.name_en && <span style={{ fontSize: 12, color: '#64748b' }}>({t.name_en})</span>}
                <Btn small danger style={{ marginLeft: 'auto' }} onClick={() => deleteTrade(t.id)}>Löschen</Btn>
              </div>
            );
          })}
        </div>
      ) : (
        /* Normaler Baum */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {roots.map(branche => (
            <div key={branche.id} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>{branche.name}</span>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>Branche</span>
                <Btn small danger style={{ marginLeft: 'auto' }} onClick={() => deleteTrade(branche.id)}>Löschen</Btn>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {children(branche.id).map(gewerk => (
                  <span key={gewerk.id} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    background: '#e0f2fe', color: '#0369a1',
                    borderRadius: 99, fontSize: 12, padding: '3px 12px',
                  }}>
                    {gewerk.name}
                    <span style={{ cursor: 'pointer', opacity: .6 }} onClick={() => deleteTrade(gewerk.id)}>×</span>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AddTradeForm({ trades, makeReq, onSaved, onCancel }) {
  const [name, setName]       = useState('');
  const [nameEn, setNameEn]   = useState('');
  const [parentId, setParent] = useState('');
  const [saving, setSaving]   = useState(false);

  const roots = trades.filter(t => !t.parent_id);

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await makeReq('/domain-knowledge/trades', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: name.trim(), name_en: nameEn.trim() || null, parent_id: parentId ? Number(parentId) : null }),
    });
    setSaving(false);
    onSaved();
  };

  return (
    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 16, marginBottom: 12 }}>
      <h4 style={{ margin: '0 0 10px', fontSize: 14 }}>Neues Gewerk / Branche</h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <input value={name}   onChange={e => setName(e.target.value)}   placeholder="Name (Deutsch)"  style={inputStyle} />
        <input value={nameEn} onChange={e => setNameEn(e.target.value)} placeholder="Name (Englisch, optional)" style={inputStyle} />
        <select value={parentId} onChange={e => setParent(e.target.value)} style={inputStyle}>
          <option value="">— Branche (kein Elternelement) —</option>
          {roots.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn primary onClick={save} disabled={saving}>{saving ? '…' : 'Speichern'}</Btn>
          <Btn onClick={onCancel}>Abbrechen</Btn>
        </div>
      </div>
    </div>
  );
}

// ─── KI-Vorschläge ────────────────────────────────────────────────────────────
function SuggestionsSection({ makeReq, showToast, onCountChange }) {
  const [suggestions, setSuggestions]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [canonicalNames, setCanonicals] = useState({});  // id → string

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await makeReq('/domain-knowledge/suggestions');
      const sugs = data.suggestions ?? [];
      setSuggestions(sugs);
      onCountChange?.(sugs.length);
    } catch { showToast('Ladefehler', 'error'); }
    finally { setLoading(false); }
  }, [makeReq, showToast]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    const canonical = (canonicalNames[id] ?? '').trim();
    if (!canonical) { showToast('Kanonischen Namen eingeben', 'error'); return; }
    await makeReq(`/domain-knowledge/suggestions/${id}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ canonical_name: canonical }),
    });
    showToast('Vorschlag bestätigt ✓');
    load();
  };

  const reject = async (id) => {
    await makeReq(`/domain-knowledge/suggestions/${id}/reject`, { method: 'POST' });
    showToast('Vorschlag abgelehnt');
    load();
  };

  if (loading) return <Spinner />;

  if (suggestions.length === 0) {
    return <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>✓ Keine offenen Vorschläge</div>;
  }

  return (
    <div>
      <h3 style={{ marginTop: 0, fontSize: 16 }}>Offene KI-Vorschläge ({suggestions.length})</h3>
      <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
        Diese Hersteller/Entitäten wurden automatisch erkannt und warten auf deine Bestätigung.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {suggestions.map(s => {
          const data = (() => { try { return JSON.parse(s.parsed_json); } catch { return {}; } })();
          return (
            <div key={s.id} style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 16px' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {data.type === 'manufacturer' ? '🏭 Hersteller' : '📦 Entität'}: <em>{s.raw_input}</em>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
                    Normalisiert: <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4 }}>{data.normalized}</code>
                    &nbsp;·&nbsp;{new Date(s.created_at).toLocaleDateString('de-DE')}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    value={canonicalNames[s.id] ?? data.suggested_canonical ?? ''}
                    onChange={e => setCanonicals(c => ({ ...c, [s.id]: e.target.value }))}
                    placeholder="Kanonischer Name"
                    style={{ ...inputStyle, width: 180, padding: '5px 10px' }}
                  />
                  <Btn primary onClick={() => approve(s.id)}>Bestätigen</Btn>
                  <Btn danger  onClick={() => reject(s.id)}>Ablehnen</Btn>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Import ───────────────────────────────────────────────────────────────────
function ImportSection({ makeReq, showToast, onImportDone, onSwitchTo }) {
  const [mode, setMode]               = useState('text');  // 'text' | 'document'
  const [text, setText]               = useState('');
  const [file, setFile]               = useState(null);
  const [preview, setPreview]         = useState(null);
  const [loading, setLoading]         = useState(false);
  const [imported, setImported]       = useState(null);
  // Automatisch extrahierte Synonyme nach Dokument-Import
  const [synSuggestions, setSynSuggestions] = useState(null);
  const [synSelected, setSynSelected]       = useState({});
  const [synSaving, setSynSaving]           = useState(false);

  const analyze = async () => {
    setLoading(true);
    setPreview(null);
    setImported(null);
    try {
      let data;
      if (mode === 'text') {
        data = await makeReq('/domain-knowledge/import/text', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, save: false }),
        });
      } else {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('save', '0');
        data = await makeReq('/domain-knowledge/import/document', { method: 'POST', body: fd });
      }
      setPreview(data.preview);
    } catch (e) { showToast('Analyse fehlgeschlagen', 'error'); }
    finally { setLoading(false); }
  };

  const save = async () => {
    setLoading(true);
    try {
      let data;
      if (mode === 'text') {
        data = await makeReq('/domain-knowledge/import/text', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, save: true, preview_json: preview }),
        });
      } else {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('save', '1');
        fd.append('preview_json', JSON.stringify(preview));
        data = await makeReq('/domain-knowledge/import/document', { method: 'POST', body: fd });
      }

      // Fehler-Erkennung: Backend kann 200 + error-Feld zurückgeben
      if (data?.error) {
        showToast('Import fehlgeschlagen: ' + data.error, 'error');
        return;
      }

      const stats = data?.stats ?? data;
      setImported(stats);
      setPreview(null);

      // Counts in Nav sofort aktualisieren
      onImportDone?.();

      const newCount = Object.values(stats || {}).reduce((s, v) => s + (Number(v) || 0), 0);
      showToast(`Import abgeschlossen — ${newCount} Einträge gespeichert ✓`);

      // Nach Dokument-Import: Synonyme automatisch extrahieren (im Hintergrund, kein Pflichtschritt)
      if (mode === 'document' && file) {
        try {
          const fd2 = new FormData();
          fd2.append('file', file);
          const synRes = await makeReq('/domain-knowledge/synonyms/extract-document', { method: 'POST', body: fd2 });
          if (synRes.entries?.length > 0) {
            setSynSuggestions(synRes);
            const sel = {};
            synRes.entries.forEach((e, i) => { if (!e.already_exists) sel[i] = true; });
            setSynSelected(sel);
          }
        } catch { /* optional — kein Fehler zeigen */ }
      }
    } catch (e) {
      showToast('Import fehlgeschlagen: ' + (e.message || 'Unbekannter Fehler'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const saveSynonyms = async () => {
    const toSave = (synSuggestions?.entries || []).filter((_, i) => synSelected[i]);
    if (!toSave.length) return;
    setSynSaving(true);
    for (const entry of toSave) {
      await makeReq('/domain-knowledge/synonyms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trigger_term: entry.trigger_term, expansions: entry.expansions, trade: entry.trade || null, language: entry.language }),
      }).catch(() => {});
    }
    showToast(`${toSave.length} Synonyme gespeichert ✓`);
    setSynSuggestions(null);
    setSynSaving(false);
  };

  return (
    <div>
      <h3 style={{ marginTop: 0, fontSize: 16 }}>Wissen importieren</h3>
      <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
        Gib Rohtext ein oder lade ein Dokument hoch (TXT, CSV, PDF, DOCX).
        HejVi analysiert den Inhalt und extrahiert Hersteller, Objekt-Typen und Varianten.
      </p>

      {/* Modus-Auswahl */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Btn primary={mode === 'text'}     onClick={() => setMode('text')}>✏️ Rohtext</Btn>
        <Btn primary={mode === 'document'} onClick={() => setMode('document')}>📄 Dokument</Btn>
      </div>

      {mode === 'text' && (
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={`Beispiele:\n\nHersteller: bito (auch: bito AG, Bito Aktiengesellschaft) — Farbenhersteller\n\nPorsche 911 (auch: Elfer, Neunelfer)\nVarianten:\n- F-Modell (1963–1973)\n- G-Modell (1973–1989): Karosserie: Coupe/Targa/Cabrio, Motor: 2.7/3.0/3.2\n- 964 (1989–1994)\n- 993 (1993–1998)\n\nGewerke: KFZ-Technik, Maler und Lackierer`}
          style={{ ...inputStyle, height: 220, fontFamily: 'monospace', fontSize: 12, resize: 'vertical', width: '100%', boxSizing: 'border-box' }}
        />
      )}

      {mode === 'document' && (
        <div
          style={{
            border: '2px dashed #cbd5e1', borderRadius: 10, padding: 30,
            textAlign: 'center', cursor: 'pointer', background: '#f8fafc',
          }}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); setFile(e.dataTransfer.files[0]); }}
          onClick={() => document.getElementById('kb-file-input').click()}
        >
          <input id="kb-file-input" type="file" accept=".txt,.csv,.md,.pdf,.docx" style={{ display: 'none' }}
            onChange={e => setFile(e.target.files[0])} />
          {file
            ? <span style={{ color: '#2563eb', fontWeight: 500 }}>📄 {file.name}</span>
            : <span style={{ color: '#94a3b8' }}>Datei hierher ziehen oder klicken<br /><small>TXT · CSV · MD · PDF · DOCX</small></span>
          }
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <Btn primary onClick={analyze} disabled={loading || (mode === 'text' ? !text.trim() : !file)}>
          {loading ? '⏳ Analysiere…' : '🔍 Analysieren'}
        </Btn>
        {preview && <Btn onClick={save} disabled={loading} style={{ background: '#16a34a', color: '#fff' }}>💾 In Wissensbasis speichern</Btn>}
      </div>

      {/* Vorschau */}
      {preview && <ImportPreview preview={preview} />}

      {/* Ergebnis */}
      {imported && (
        <div style={{ marginTop: 16, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 16 }}>
          <div style={{ fontWeight: 600, color: '#15803d', marginBottom: 10 }}>✓ Import abgeschlossen</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            {Object.entries(imported).map(([k, v]) => (
              <div key={k} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#16a34a' }}>{v}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{k}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Btn small onClick={() => onSwitchTo?.('manufacturers')}>🏭 Hersteller prüfen</Btn>
            <Btn small onClick={() => onSwitchTo?.('object-types')}>📦 Objekt-Typen prüfen</Btn>
          </div>
        </div>
      )}

      {/* Automatisch extrahierte Synonyme — erscheinen nur nach Dokument-Import */}
      {synSuggestions && (
        <SynonymSuggestions
          suggestions={synSuggestions}
          selected={synSelected}
          setSelected={setSynSelected}
          onSave={saveSynonyms}
          saving={synSaving}
          onDismiss={() => setSynSuggestions(null)}
        />
      )}
    </div>
  );
}

function ImportPreview({ preview }) {
  const { manufacturers = [], object_types = [], variants = [], trades = [] } = preview;

  return (
    <div style={{ marginTop: 16, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 12 }}>🔍 Vorschau — erkannte Entitäten</div>

      {manufacturers.length > 0 && (
        <PreviewGroup label="🏭 Hersteller" count={manufacturers.length}>
          {manufacturers.map((m, i) => (
            <div key={i} style={{ fontSize: 13, marginBottom: 4 }}>
              <strong>{m.canonical_name}</strong>
              {m.aliases?.length > 0 && <> → Aliases: {m.aliases.join(', ')}</>}
              {m.description && <span style={{ color: '#64748b' }}> · {m.description}</span>}
            </div>
          ))}
        </PreviewGroup>
      )}

      {object_types.length > 0 && (
        <PreviewGroup label="📦 Objekt-Typen" count={object_types.length}>
          {object_types.map((o, i) => (
            <div key={i} style={{ fontSize: 13, marginBottom: 4 }}>
              <strong>{o.name}</strong>
              {o.manufacturer && <span style={{ color: '#2563eb' }}> ({o.manufacturer})</span>}
              {o.aliases?.length > 0 && <> · Aliases: {o.aliases.join(', ')}</>}
            </div>
          ))}
        </PreviewGroup>
      )}

      {variants.length > 0 && (
        <PreviewGroup label="🔖 Varianten" count={variants.length}>
          {variants.map((v, i) => (
            <div key={i} style={{ fontSize: 13, marginBottom: 4 }}>
              <strong>{v.object_type}</strong> → {v.name}
              {v.aliases?.length > 0 && <> ({v.aliases.join(', ')})</>}
              {v.attributes && Object.keys(v.attributes).length > 0 && (
                <span style={{ color: '#64748b' }}> · {Object.entries(v.attributes).map(([k,val]) => `${k}: ${val}`).join(', ')}</span>
              )}
            </div>
          ))}
        </PreviewGroup>
      )}

      {trades.length > 0 && (
        <PreviewGroup label="🔧 Gewerke" count={trades.length}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {trades.map((t, i) => <Tag key={i}>{t}</Tag>)}
          </div>
        </PreviewGroup>
      )}

      {manufacturers.length + object_types.length + variants.length + trades.length === 0 && (
        <div style={{ color: '#92400e', fontSize: 13 }}>⚠ Keine Entitäten erkannt. Bitte Text anpassen.</div>
      )}
    </div>
  );
}

function PreviewGroup({ label, count, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e', marginBottom: 6 }}>{label} ({count})</div>
      {children}
    </div>
  );
}

// ─── Synonyme ─────────────────────────────────────────────────────────────────
const SYNONYM_LANGS = [
  { code: 'de', label: 'Deutsch' }, { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' }, { code: 'es', label: 'Español' },
  { code: 'ru', label: 'Русский' }, { code: 'pl', label: 'Polski' },
  { code: 'ar', label: 'عربي' },   { code: 'ro', label: 'Română' },
  { code: 'tr', label: 'Türkçe' }, { code: 'hr', label: 'Hrvatski' },
];

function SynonymsSection({ makeReq, showToast, onCountChange }) {
  const [synonyms, setSynonyms]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [trades, setTrades]             = useState([]);
  const [filterLang, setFilterLang]     = useState('all');
  const [panel, setPanel]               = useState(null); // null | 'add' | 'catalog'
  const [editEntry, setEditEntry]       = useState(null);
  const [deleteId, setDeleteId]         = useState(null);
  const { q, setQ, matches }            = useSearch();

  // Add/Edit form
  const [form, setForm]                 = useState({ trigger_term: '', expansions: '', trade: '', language: 'de', autoTranslate: true });
  const [suggesting, setSuggesting]     = useState(false);
  const [saving, setSaving]             = useState(false);

  // Catalog import
  const [catFile, setCatFile]           = useState(null);
  const [catParsed, setCatParsed]       = useState(null);
  const [catOverwrite, setCatOverwrite] = useState(false);
  const [catImporting, setCatImporting] = useState(false);
  const [catResult, setCatResult]       = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [synData, ctxData] = await Promise.allSettled([
        makeReq('/domain-knowledge/synonyms'),
        makeReq('/domain-knowledge/synonyms/trade-context'),
      ]);
      const syns = synData.status === 'fulfilled' ? (synData.value.synonyms || []) : [];
      setSynonyms(syns);
      onCountChange?.(syns.length);
      if (ctxData.status === 'fulfilled') setTrades(ctxData.value.trades || []);
    } catch { showToast('Ladefehler Synonyme', 'error'); }
    finally { setLoading(false); }
  }, [makeReq, showToast, onCountChange]);

  useEffect(() => { load(); }, [load]);

  const openAdd = (entry = null) => {
    setEditEntry(entry);
    setForm(entry
      ? { trigger_term: entry.trigger_term, expansions: entry.expansions, trade: entry.trade || '', language: entry.language, autoTranslate: false }
      : { trigger_term: '', expansions: '', trade: '', language: 'de', autoTranslate: true }
    );
    setPanel('add');
  };

  const suggest = async () => {
    if (!form.trigger_term.trim()) return;
    setSuggesting(true);
    try {
      const res = await makeReq('/domain-knowledge/synonyms/suggest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trigger_term: form.trigger_term.trim(), language: form.language, trade: form.trade }),
      });
      setForm(f => ({ ...f, expansions: res.expansions_string || '' }));
    } catch { showToast('Vorschlag fehlgeschlagen', 'error'); }
    finally { setSuggesting(false); }
  };

  const saveEntry = async () => {
    if (!form.trigger_term.trim() || !form.expansions.trim()) {
      showToast('Auslöser und Expansions erforderlich', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        trigger_term: form.trigger_term.trim(),
        expansions:   form.expansions.trim(),
        trade:        form.trade.trim() || null,
        language:     form.language,
      };
      if (editEntry) {
        await makeReq(`/domain-knowledge/synonyms/${editEntry.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
        });
        showToast('Aktualisiert ✓');
      } else {
        await makeReq('/domain-knowledge/synonyms', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
        });
        if (form.autoTranslate) {
          setSaving('translating');
          const tRes = await makeReq('/domain-knowledge/synonyms/translate-all', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...payload, source_language: form.language }),
          }).catch(() => ({}));
          const n = (tRes.created || 0) + (tRes.skipped || 0);
          showToast(`Gespeichert + ${n} Sprachen übersetzt ✓`);
        } else {
          showToast('Gespeichert ✓');
        }
      }
      setPanel(null);
      load();
    } catch { showToast('Fehler beim Speichern', 'error'); }
    finally { setSaving(false); }
  };

  const deleteEntry = async (id) => {
    await makeReq(`/domain-knowledge/synonyms/${id}`, { method: 'DELETE' });
    showToast('Gelöscht');
    setDeleteId(null);
    load();
  };

  const parseCatFile = (file) => {
    setCatFile(file); setCatResult(null); setCatParsed(null);
    if (!file?.name.endsWith('.json')) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        const raw  = data.entries || data.synonyms || (Array.isArray(data) ? data : []);
        setCatParsed({ count: raw.filter(r => r.trigger_term && r.expansions).length });
      } catch { setCatParsed({ count: '?' }); }
    };
    reader.readAsText(file);
  };

  const importCatalog = async () => {
    if (!catFile) return;
    setCatImporting(true); setCatResult(null);
    try {
      const fd = new FormData();
      fd.append('file', catFile);
      if (catOverwrite) fd.append('overwrite', '1');
      const res = await makeReq('/domain-knowledge/synonyms/import-catalog', { method: 'POST', body: fd });
      setCatResult(res);
      showToast(`Import: ${res.created} neu, ${res.skipped} übersprungen`);
      load();
    } catch { showToast('Import fehlgeschlagen', 'error'); }
    finally { setCatImporting(false); }
  };

  if (loading) return <Spinner />;

  const visible = synonyms.filter(e =>
    (filterLang === 'all' || e.language === filterLang) &&
    matches([e.trigger_term, e.expansions, e.trade])
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Suchbegriff-Synonyme ({synonyms.length})</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn primary={panel === 'add'} onClick={() => { openAdd(); }}>✏️ Eintrag</Btn>
          <Btn primary={panel === 'catalog'} onClick={() => setPanel(p => p === 'catalog' ? null : 'catalog')}>📦 Katalog</Btn>
        </div>
      </div>
      <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
        Auslöser-Begriff gefunden in Query → verwandte Suchbegriffe werden vor der Vektorisierung angehängt.
        Verbessert die Trefferquote bei prozess- und produktspezifischen Fragen.
      </p>

      {/* Panel: Neu/Bearbeiten */}
      {panel === 'add' && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <h4 style={{ margin: '0 0 12px', fontSize: 14 }}>{editEntry ? 'Eintrag bearbeiten' : 'Neuer Eintrag'}</h4>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 10 }}>
            {/* Trigger + Suggest-Button */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Auslöser-Begriff *</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input value={form.trigger_term} style={{ ...inputStyle, flex: 1 }}
                  placeholder='z.B. „anmischen"'
                  onChange={e => setForm(f => ({ ...f, trigger_term: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter' && form.trigger_term.trim()) suggest(); }} />
                <Btn onClick={suggest} disabled={suggesting || !form.trigger_term.trim()}
                  title="Ähnliche Begriffe vorschlagen" style={{ flexShrink: 0 }}>
                  {suggesting ? '⟳' : '💡 Vorschlagen'}
                </Btn>
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>Enter oder „Vorschlagen" = Verwandte Begriffe automatisch finden</div>
            </div>

            {/* Expansions */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                Verwandte Suchbegriffe * <span style={{ fontWeight: 400, color: '#94a3b8' }}>(kommagetrennt)</span>
              </label>
              <textarea value={form.expansions} style={{ ...inputStyle, minHeight: 80, resize: 'vertical', fontFamily: 'inherit' }}
                placeholder='z.B. „Mischungsverhältnis, Anmischung, Vorbereitung, mixing ratio"'
                onChange={e => setForm(f => ({ ...f, expansions: e.target.value }))} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {/* Sprache */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Sprache *</label>
                <select value={form.language} style={inputStyle} onChange={e => setForm(f => ({ ...f, language: e.target.value }))}>
                  {SYNONYM_LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                </select>
              </div>

              {/* Branche */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  Branche <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span>
                </label>
                {trades.length > 0 ? (
                  <select value={form.trade} style={inputStyle} onChange={e => setForm(f => ({ ...f, trade: e.target.value }))}>
                    <option value="">–</option>
                    {trades.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                ) : (
                  <input value={form.trade} style={inputStyle} placeholder='z.B. „Bau"'
                    onChange={e => setForm(f => ({ ...f, trade: e.target.value }))} />
                )}
              </div>
            </div>
          </div>

          {!editEntry && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer', fontSize: 13 }}>
              <input type="checkbox" checked={form.autoTranslate} onChange={e => setForm(f => ({ ...f, autoTranslate: e.target.checked }))} />
              🌐 In alle 9 weiteren Sprachen übersetzen
            </label>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <Btn primary onClick={saveEntry} disabled={!!saving}>
              {saving === 'translating' ? '⟳ Übersetze…' : saving ? '⟳ Speichert…' : 'Speichern'}
            </Btn>
            <Btn onClick={() => setPanel(null)}>Abbrechen</Btn>
          </div>
        </div>
      )}

      {/* Panel: Katalog-Import */}
      {panel === 'catalog' && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>📦 Synonymkatalog importieren</h4>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
            HejVi-JSON: <code style={{ fontSize: 11, background: '#dcfce7', padding: '1px 6px', borderRadius: 4 }}>
              {'{"entries":[{"trigger_term":"...","expansions":"...","trade":"...","language":"de"}]}'}
            </code>
            <span style={{ margin: '0 8px' }}>·</span>
            CSV: <code style={{ fontSize: 11, background: '#dcfce7', padding: '1px 6px', borderRadius: 4 }}>trigger_term,expansions,trade,language</code>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13, border: '1px dashed #86efac', background: '#f0fdf4', color: '#15803d' }}>
              <input type="file" accept=".json,.csv" style={{ display: 'none' }} onChange={e => parseCatFile(e.target.files[0] || null)} />
              📎 {catFile ? catFile.name : 'Datei auswählen (.json · .csv)'}
            </label>
            {catParsed && <span style={{ fontSize: 13, color: '#475569' }}>📊 {catParsed.count} Einträge</span>}
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={catOverwrite} onChange={e => setCatOverwrite(e.target.checked)} />
            Bestehende Einträge überschreiben
          </label>

          {catResult && (
            <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 8, background: '#dcfce7', fontSize: 13 }}>
              ✅ <b>{catResult.created}</b> neu angelegt · <b>{catResult.skipped}</b> übersprungen
              {catResult.errors?.length > 0 && <span style={{ color: '#dc2626', marginLeft: 8 }}>{catResult.errors.length} Fehler</span>}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <Btn primary onClick={importCatalog} disabled={!catFile || catImporting}>
              {catImporting ? '⟳ Importiere…' : '📥 Importieren'}
            </Btn>
            <Btn onClick={() => { setPanel(null); setCatFile(null); setCatParsed(null); setCatResult(null); }}>Abbrechen</Btn>
          </div>
        </div>
      )}

      {/* Sprach-Filter */}
      <div style={{ display: 'flex', gap: 5, marginBottom: 10, flexWrap: 'wrap' }}>
        {[{ code: 'all', label: `Alle (${synonyms.length})` }, ...SYNONYM_LANGS.map(l => {
          const cnt = synonyms.filter(e => e.language === l.code).length;
          return cnt > 0 ? { code: l.code, label: `${l.label} (${cnt})` } : null;
        }).filter(Boolean)].map(l => (
          <button key={l.code} onClick={() => setFilterLang(l.code)} style={{
            padding: '3px 11px', borderRadius: 99, border: 'none', cursor: 'pointer', fontSize: 12,
            background: filterLang === l.code ? '#2563eb' : '#f1f5f9',
            color:      filterLang === l.code ? '#fff'    : '#374151',
            fontWeight: filterLang === l.code ? 700 : 400,
          }}>{l.label}</button>
        ))}
      </div>

      <SearchBar q={q} setQ={setQ} placeholder="Synonym suchen…" count={visible.length} total={synonyms.length} />

      {/* Eintrags-Tabelle */}
      {visible.length === 0 && !q && synonyms.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>📭</div>
          <div style={{ fontSize: 13 }}>Noch keine Synonyme — Eintrag anlegen oder Katalog importieren.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {visible.map(entry => (
            <div key={entry.id} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 5 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{entry.trigger_term}</span>
                    <span style={{ fontSize: 11, color: '#64748b', background: '#e2e8f0', borderRadius: 4, padding: '1px 6px' }}>
                      {SYNONYM_LANGS.find(l => l.code === entry.language)?.label || entry.language}
                    </span>
                    {entry.trade && <Tag muted>{entry.trade}</Tag>}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {entry.expansions.split(',').map((t, i) => <Tag key={i}>{t.trim()}</Tag>)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <Btn small onClick={() => openAdd(entry)}>Bearb.</Btn>
                  {deleteId === entry.id ? (
                    <>
                      <Btn small danger onClick={() => deleteEntry(entry.id)}>Löschen?</Btn>
                      <Btn small onClick={() => setDeleteId(null)}>Abbruch</Btn>
                    </>
                  ) : (
                    <Btn small danger onClick={() => setDeleteId(entry.id)}>Löschen</Btn>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Zeigt automatisch extrahierte Synonym-Vorschläge nach einem Dokument-Import.
 * Wird von ImportSection eingeblendet wenn extract-document Ergebnisse liefert.
 */
function SynonymSuggestions({ suggestions, selected, setSelected, onSave, saving, onDismiss }) {
  const newCount = (suggestions.entries || []).filter((_, i) => selected[i]).length;

  return (
    <div style={{ marginTop: 16, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>
          🔍 {suggestions.total} Synonyme erkannt
          {suggestions.already_exist > 0 && (
            <span style={{ marginLeft: 8, fontSize: 12, color: '#94a3b8', fontWeight: 400 }}>
              {suggestions.already_exist} bereits vorhanden
            </span>
          )}
        </div>
        <Btn small onClick={onDismiss}>Verwerfen</Btn>
      </div>

      <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#dbeafe' }}>
            <th style={{ padding: '5px 8px', width: 28 }}>
              <input type="checkbox"
                checked={newCount > 0}
                onChange={e => {
                  const s = {};
                  (suggestions.entries || []).forEach((en, i) => { if (!en.already_exists) s[i] = e.target.checked; });
                  setSelected(s);
                }} />
            </th>
            {['Auslöser', 'Expansions', 'Branche', 'Spr.', 'Status'].map((h, i) => (
              <th key={i} style={{ padding: '5px 8px', textAlign: 'left', fontWeight: 600, color: '#1d4ed8' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(suggestions.entries || []).map((entry, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #dbeafe', opacity: entry.already_exists ? 0.4 : 1 }}>
              <td style={{ padding: '5px 8px' }}>
                <input type="checkbox" disabled={entry.already_exists}
                  checked={!!selected[i]}
                  onChange={e => setSelected(prev => ({ ...prev, [i]: e.target.checked }))} />
              </td>
              <td style={{ padding: '5px 8px', fontWeight: 600 }}>{entry.trigger_term}</td>
              <td style={{ padding: '5px 8px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  {entry.expansions.split(',').map((t, j) => <Tag key={j}>{t.trim()}</Tag>)}
                </div>
              </td>
              <td style={{ padding: '5px 8px', color: '#94a3b8' }}>{entry.trade || '–'}</td>
              <td style={{ padding: '5px 8px', color: '#64748b' }}>{entry.language}</td>
              <td style={{ padding: '5px 8px' }}>
                {entry.already_exists
                  ? <span style={{ color: '#94a3b8' }}>↩ vorh.</span>
                  : <span style={{ color: '#16a34a' }}>✓ neu</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
        <Btn primary onClick={onSave} disabled={saving || newCount === 0}>
          {saving ? '⟳ Speichert…' : `💾 ${newCount} Synonyme speichern`}
        </Btn>
        <Btn onClick={onDismiss}>Verwerfen</Btn>
      </div>
    </div>
  );
}

// ─── Micro-Komponenten ────────────────────────────────────────────────────────
const inputStyle = {
  border: '1px solid #e2e8f0', borderRadius: 7, padding: '7px 12px',
  fontSize: 13, width: '100%', boxSizing: 'border-box', outline: 'none',
};

function Btn({ children, onClick, primary, danger, small, disabled, style = {} }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding:       small ? '4px 10px' : '8px 16px',
        borderRadius:  8,
        border:        'none',
        cursor:        disabled ? 'not-allowed' : 'pointer',
        fontSize:      small ? 12 : 13,
        fontWeight:    500,
        background:    disabled ? '#e2e8f0' : primary ? '#2563eb' : danger ? '#dc2626' : '#f1f5f9',
        color:         disabled ? '#94a3b8' : primary || danger ? '#fff' : '#374151',
        opacity:       disabled ? .7 : 1,
        whiteSpace:    'nowrap',
        ...style,
      }}
    >{children}</button>
  );
}

function Tag({ children, muted }) {
  return (
    <span style={{
      background:   muted ? '#f1f5f9' : '#dbeafe',
      color:        muted ? '#64748b' : '#1d4ed8',
      borderRadius: 99, fontSize: 11, padding: '2px 8px', whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

function Spinner() {
  return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>⏳ Lädt…</div>;
}
